import React, { useContext, useEffect, useState } from 'react';
import { EllipsisVerticalIcon, EyeIcon, PencilIcon, PrinterIcon } from '@heroicons/react/16/solid';
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from '../../../components/Dropdown';
import { Link } from '../../../components/Link';
import { Button } from '../../../components/Button';
import { Heading } from '../../../components/Heading';
import Loading from '../../../components/Loading';
import { useNavigate } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/Table';
import dateFormat from 'dateformat';
import { UserContext } from '../../../context/userContext';
import { WarehouseProduct } from '../../../models/WarehouseProduct';
import { useWarehouseProductItem } from '../../../hooks/useWarehouseProductItem';
import { WarehouseProductItem } from '../../../models/WarehouseProductItem';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import ExcelJS from 'exceljs';
import useWarehouseProductItemMovement from '../../../hooks/useWarehouseProductItemMovement';
import { WarehouseProductItemMovement } from '../../../models/WarehouseProductItemMovement';
import useWarehouseProductItemBatchNoMovement from '../../../hooks/useWarehouseProductItemBatchNoMovement';
import { WarehouseProductItemBatchNoMovement } from '../../../models/WarehouseProductItemBatchNoMovement';
import { useSetupUsergroup } from '../../../hooks/useSetupUsergroup';
import showErrorToast from '../../../components/Toasts/ShowErrorToast';
import { Text } from '../../../components/Text';
import { Card } from '../../../components/Card';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu, ColumnsMenu, isColumnVisible } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { DateRangePicker } from '../../../components/DateRangePicker';
import { Select } from '../../../components/Select';

const COLUMNS = [
    { key: 'secondItemNo', label: 'Second Item No' },
    { key: 'description', label: 'Description' },
    { key: 'qtyIn', label: 'Total Quantity In' },
    { key: 'qtyOut', label: 'Total Quantity Out' },
    { key: 'qtyAdjust', label: 'Total Quantity Adjust' },
    { key: 'qtyTransfer', label: 'Total Quantity Transfer' },
    { key: 'supplier', label: 'Supplier' },
    { key: 'lastMovement', label: 'Last Movement Date Time' },
];
const SORT_OPTIONS = [
    { label: 'Item No (A-Z)', value: 'az' },
    { label: 'Item No (Z-A)', value: 'za' },
];

const ReportStockByBatchPage: React.FC = () => {
    const { jsPDF } = require('jspdf');
    require('jspdf-autotable');
    const { currentUser } = useContext(UserContext); // Get current user info
    const {
        loading,
        error,
        batchNoMovements
    } = useWarehouseProductItemBatchNoMovement();
    const { suppliers } = useSetupUsergroup();
    const [searchQuery, setSearchQuery] = useState<string>(''); // State to store the search query
    const [filterStartDate, setFilterStartDate] = useState<string>('');
    const [filterEndDate, setFilterEndDate] = useState<string>('');
    const [filterSupplierId, setFilterSupplierId] = useState<string>('');
    const [filterYear, setFilterYear] = useState<string>('');
    const navigate = useNavigate();
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [sortOrder, setSortOrder] = useState('az');
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    const toggleColumn = (key: string) => setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));

    const resetFilters = () => {
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterYear('');
        setFilterSupplierId('');
    };

    const availableYears = Array.from(
        new Set(
            (Array.isArray(batchNoMovements) ? batchNoMovements : [])
                .map((m) => new Date(m.created_time!).getFullYear())
                .filter((year) => !Number.isNaN(year))
        )
    ).sort((a, b) => b - a);

    const activeFilterCount = [filterStartDate, filterEndDate, filterYear, filterSupplierId].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterStartDate ? [{ key: 'from', label: `From: ${filterStartDate}`, onClear: () => setFilterStartDate('') }] : []),
        ...(filterEndDate ? [{ key: 'to', label: `To: ${filterEndDate}`, onClear: () => setFilterEndDate('') }] : []),
        ...(filterYear ? [{ key: 'year', label: `Year: ${filterYear}`, onClear: () => setFilterYear('') }] : []),
        ...(filterSupplierId ? [{
            key: 'supplier',
            label: `Supplier: ${suppliers?.find((s) => s.id?.toString() === filterSupplierId)?.fullname || filterSupplierId}`,
            onClear: () => setFilterSupplierId(''),
        }] : []),
    ];

    useEffect(() => {
        document.title = "Stock By Batch Report";
    }, []);

    const formatDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Filter logic
    const filteredProductItemMovement = Array.isArray(batchNoMovements)
        ? batchNoMovements.filter((productItemMovement) => {
            const itemDate = new Date(productItemMovement.created_time!);
            const matchesSearch =
                productItemMovement.batch_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                productItemMovement.item_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                productItemMovement.second_item_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                productItemMovement.description?.toLowerCase().includes(searchQuery.toLowerCase());

            const matchesDate =
                (!filterStartDate || itemDate >= new Date(filterStartDate)) &&
                (!filterEndDate || itemDate <= new Date(filterEndDate));

            const matchesYear =
                !filterYear || itemDate.getFullYear().toString() === filterYear;

            const matchesSupplier =
                !filterSupplierId || productItemMovement.supplier_id?.toString() === filterSupplierId;

            return matchesSearch && matchesDate && matchesYear && matchesSupplier;
        })
        : [];

    // Function to generate PDF (landscape)
    const generatePDF = () => {
        const today = formatDate(new Date());

        if (!batchNoMovements || batchNoMovements.length === 0) {
            showErrorToast("PDF generation failed. No data available.");
            return;
        }

        // Create landscape PDF (width > height)
        const doc = new jsPDF('landscape');
        doc.setFontSize(12);
        doc.text(`Stock By Batch Report | ${today}`, 14, 10);

        // AutoTable configuration for landscape
        doc.autoTable({
            startY: 20,
            head: [['S/N', 'Batch No', 'Item No', 'Second Item No', 'Description', 'Qty In', 'Qty Out', 'Qty Adjust', 'Qty Transfer', 'Supplier', 'Last Movement Date Time']],
            body: filteredProductItemMovement.map((item, index) => [
                index + 1,
                item.batch_no || "N/A",
                item.item_no || "N/A",
                item.second_item_no || "N/A",
                item.description || "N/A",
                item.total_quantity_in || 0,
                item.total_quantity_out || 0,
                item.total_quantity_adjust || 0,
                item.total_quantity_transfer || 0,
                item.supplier_fullname || "N/A",
                dateFormat(item.updated_time?.toString(), "mmm dd, yyyy hh:mm:ss")
            ]),
            styles: {
                fontSize: 8, // Smaller font for landscape
                cellPadding: 2 // Tighter padding
            },
            headStyles: {
                fillColor: [22, 163, 74] // Green header
            },
            columnStyles: {
                0: { cellWidth: 'auto' }, // S/N
                1: { cellWidth: 'auto' }, // Batch No
                2: { cellWidth: 'auto' }, // Item No
                3: { cellWidth: 'auto' }, // Second Item No
                4: { cellWidth: 40 }, // Description (wider)
                5: { cellWidth: 'auto' }, // Qty In
                6: { cellWidth: 'auto' }, // Qty Out
                7: { cellWidth: 'auto' }, // Qty Adjust
                8: { cellWidth: 'auto' }, // Qty Transfer
                9: { cellWidth: 'auto' }, // Supplier
                10: { cellWidth: 'auto' } // Last Movement
            }
        });

        doc.save(`stock_by_batch_report_${today}.pdf`);
    };

    // Function to generate CSV
    const generateCSV = () => {
        if (!batchNoMovements) {
            showErrorToast("CSV generation failed. Product item is not available.");
            return; // Exit the function early if productItem is undefined
        }

        const csvData = [
            ['S/N', 'Batch No', 'Item No', 'Second Item No', 'Description', 'Total Quantity In', 'Total Quantity Out', 'Total Quantity Adjust'],
            ...batchNoMovements.map((item, index) => [
                index + 1,
                item.batch_no || "N/A",
                item.item_no || "N/A",
                item.second_item_no || "N/A",
                item.description || "N/A",
                item.total_quantity_in || 0,
                item.total_quantity_out || 0,
                item.total_quantity_adjust || 0,
                item.total_quantity_transfer || 0
            ])
        ];

        const csvContent = 'data:text/csv;charset=utf-8,' + csvData.map(e => e.join(",")).join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        const today = formatDate(new Date());
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `stock_by_batch_report_${today}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Function to generate Excel
    const generateExcel = async () => {
        if (!batchNoMovements) {
            showErrorToast("Excel generation failed. Product item is not available.");
            return;
        }

        const data = batchNoMovements.map((item, index) => ({
            SNo: index + 1,
            BatchNo: item.batch_no || "N/A",
            ItemNo: item.item_no || "N/A",
            SecondItemNo: item.second_item_no || "N/A",
            Description: item.description || "N/A",
            TotalQuantityIn: item.total_quantity_in || 0,
            TotalQuantityOut: item.total_quantity_out || 0,
            TotalQuantityAdjust: item.total_quantity_adjust || 0,
            TotalQuantityTransfer: item.total_quantity_transfer || 0
        }));

        if (data.length === 0) {
            showErrorToast("Excel generation failed. No data available.");
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet("Stock By Batch");
        sheet.columns = Object.keys(data[0]).map((key) => ({ header: key, key, width: 18 }));
        sheet.addRows(data);

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `stock_movement_report_${formatDate(new Date())}.xlsx`;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>Stock By Batch</Heading>
                </div>
                <Dropdown>
                    <DropdownButton aria-label="More options">
                        Print Report
                    </DropdownButton>
                    <DropdownMenu anchor="bottom end">
                        <DropdownItem onClick={generatePDF}>PDF</DropdownItem>
                        <DropdownItem onClick={generateCSV}>CSV</DropdownItem>
                        <DropdownItem onClick={generateExcel}>Excel</DropdownItem>
                    </DropdownMenu>
                </Dropdown>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search stock by batch…" />}>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
                <ColumnsMenu columns={COLUMNS} visible={columnVisibility} onToggle={toggleColumn} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={resetFilters} />

            <ul>
                {loading && <div className='w-full text-center h-full py-36 justify-center'><Loading /></div>}
                {error && <p className='py-16 text-center text-red-700'>{error}</p>}
                {(!loading && !error) && (
                    <div>
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>S/N</TableHeader>
                                    <TableHeader>Batch No</TableHeader>
                                    <TableHeader>Item No</TableHeader>
                                    {isColumnVisible(columnVisibility, 'secondItemNo') && <TableHeader>Second Item No</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'description') && <TableHeader>Description</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'qtyIn') && <TableHeader>Total Quantity In</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'qtyOut') && <TableHeader>Total Quantity Out</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'qtyAdjust') && <TableHeader>Total Quantity Adjust</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'qtyTransfer') && <TableHeader>Total Quantity Transfer</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'supplier') && <TableHeader>Supplier</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'lastMovement') && <TableHeader>Last Movement Date Time</TableHeader>}
                                    <TableHeader>Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(filteredProductItemMovement.length > 0) ? [...filteredProductItemMovement].sort((a, b) => sortOrder === 'az' ? (a.item_no || '').localeCompare(b.item_no || '') : (b.item_no || '').localeCompare(a.item_no || '')).map((item, index) => (
                                    <TableRow key={index} title={`${index}`}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell className="text-zinc-500">{item.batch_no || '-'}</TableCell>
                                        <TableCell className="text-zinc-500">{item.item_no || '-'}</TableCell>
                                        {isColumnVisible(columnVisibility, 'secondItemNo') && <TableCell className="text-zinc-500">{item.second_item_no || '-'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'description') && <TableCell className="text-zinc-500">{item.description || '-'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'qtyIn') && <TableCell className="text-zinc-400">{item.total_quantity_in || '0'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'qtyOut') && <TableCell className="text-zinc-400">{item.total_quantity_out || '0'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'qtyAdjust') && <TableCell className="text-zinc-400">{item.total_quantity_adjust || '0'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'qtyTransfer') && <TableCell className="text-zinc-400">{item.total_quantity_transfer || '0'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'supplier') && <TableCell className="text-zinc-400">{item.supplier_fullname || 'N/A'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'lastMovement') && <TableCell className="text-zinc-400">{dateFormat(item.updated_time?.toString(), "mmm dd, yyyy hh:mm:ss")}</TableCell>}
                                        <TableCell className="py-5 flex gap-4">
                                            <a className="text-zinc-600 hover:text-zinc-900" onClick={() => navigate(`/gp/reports/stockbybatch/view/${item.id}`)}>
                                                <EyeIcon className='h-5 w-5' />
                                            </a>
                                        </TableCell>
                                    </TableRow>
                                )) : <TableRow>
                                    <TableCell colSpan={9} className="text-center py-8">No stock by batch found.</TableCell>
                                </TableRow>}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </ul>
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                <div className="sm:col-span-2">
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={filterStartDate}
                        to={filterEndDate}
                        onChange={({ from, to }) => { setFilterStartDate(from); setFilterEndDate(to); }}
                        className="w-full"
                    />
                </div>
                <div>
                    <Text>By Year</Text>
                    <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {availableYears.map((year) => (
                            <option key={year} value={year.toString()}>{year}</option>
                        ))}
                    </Select>
                </div>
                {(currentUser?.role.toString() !== "5") && (
                    <div>
                        <Text>By Supplier</Text>
                        <Select value={filterSupplierId} onChange={(e) => setFilterSupplierId(e.target.value)}>
                            <option value="">All Suppliers</option>
                            {suppliers?.map((supplier) => (
                                <option key={supplier.id} value={supplier.id?.toString()}>
                                    {supplier.fullname}
                                </option>
                            ))}
                        </Select>
                    </div>
                )}
            </FiltersDialog>
        </>
    );
};

export default ReportStockByBatchPage;
